<!-- ImagePickerView.vue -->
<template>
  <v-card @click.stop>
    <v-card-title class="pa-0">
      <v-app-bar color="secondary" dense>
        <v-toolbar-title>{{ t('imagePicker.title') }}</v-toolbar-title>
        <v-spacer />
        <v-select
          v-model="selectedCategoryIds"
          data-cy="image-picker-select"
          :items="categories"
          hide-details
          :placeholder="t('imagePicker.filterCategories')"
          prepend-inner-icon="mdi-folder-search-outline"
          item-value="id"
          item-text="name"
          multiple
          dense
          class="category-select"
          solo-inverted
          flat
          clearable
          chips
          small-chips
          :menu-props="{ offsetY: true, contentClass: 'category-menu', maxHeight: 300 }"
        />
        <v-btn
          icon
          type="button"
          :ripple="false"
          data-cy="image-picker-close-btn"
          @click.stop.prevent="onCloseClick"
          @touchend.stop.prevent="onCloseClick"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-app-bar>
    </v-card-title>

    <v-card-text class="image-picker-scroll" @click.stop>
      <v-container>
        <v-row v-for="category in selectedCategories" :key="category.id">
          <v-list-item-title>{{ category.name }}</v-list-item-title>

          <v-col
            v-for="image in imagesInCategory(category.id)"
            :key="image.id"
            :ref="imageRefName(image.id)"
            sm="6"
            xs="12"
            md="4"
            cols="12"
          >
            <v-hover v-slot="{ isHovering }">
              <v-card
                :id="'donationImage' + image.id"
                data-cy="image-picker-card"
                tile
                class="pa-1 touch-pan-y"
                role="button"
                tabindex="0"
                @click.stop.prevent="onCardClick(image.id)"
                @keydown.enter.prevent="onPick(image.id)"
                @keydown.space.prevent="onPick(image.id)"
                @touchstart.stop="onTouchStart($event, image.id)"
                @touchmove="onTouchMove"
                @touchend.stop="onTouchEnd"
              >
                <v-img
                  data-cy="image-picker-image"
                  :src="image.url"
                  width="100%"
                  class="grey lighten-2"
                  aspect-ratio="1.5"
                />
                <v-fade-transition>
                  <div v-if="isHovering" key="hover-overlay">
                    <v-overlay color="accent" absolute />
                  </div>
                </v-fade-transition>
              </v-card>
            </v-hover>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { useLabel } from '@/composables/useLabel';
import { DonationImage } from '@/interfaces/donation/donationImage';
import { DonationImageCategory } from '@/interfaces/donation/donationImageCategory';

export default defineComponent({
  props: {
    modelValue: { type: Number, default: null },
    hideTitle: { type: Boolean, default: false },
    images: { type: Array as () => DonationImage[], default: () => [] },
    categories: { type: Array as () => DonationImageCategory[], required: true },
  },
  emits: ['update:modelValue', 'close'],
  setup(props, { emit }) {
    const categoryId = ref(null as number | null);
    const searchMode = ref(false);
    const imageId = ref(props.modelValue as number | null);
    const selectedCategoryIds = ref([] as number[]);
    const query = ref(null as string | null);
    const closing = ref(false);
    // tap vs scroll (iPhone)
    const pendingPickId = ref(null as number | null);
    const touchStartX = ref(0);
    const touchStartY = ref(0);
    const touchStartTime = ref(0);
    const touchMoved = ref(false);
    const TAP_MOVE_THRESHOLD = 10;
    const TAP_TIME_THRESHOLD = 600;

    const selectedCategories = computed(() => {
      return selectedCategoryIds.value.length
        ? props.categories.filter((c) => selectedCategoryIds.value.includes(c.id))
        : props.categories;
    });

    const isIPhone = computed(() => {
      const ua = navigator.userAgent;
      return (
        /\biPhone\b/i.test(ua) ||
        (ua.includes('Mobile') &&
          ua.includes('Safari') &&
          !ua.includes('iPad') &&
          !ua.includes('iPod'))
      );
    });

    watch(imageId, (val) => emit('update:modelValue', val));
    watch(
      () => props.modelValue,
      (val) => (imageId.value = val)
    );

    const imageRefName = (id: number) => 'image-' + id;

    const imagesInCategory = (categoryId: number) => {
      return props.images.filter((i) => i.donationImageCategory.id === categoryId);
    };

    // Non-iPhone click path
    const onCardClick = (id: number) => {
      if (isIPhone.value) return;
      onPick(id);
    };

    // iPhone tap-vs-scroll
    const onTouchStart = (e: TouchEvent, id: number) => {
      if (!isIPhone.value) return;
      const t = e.touches[0];
      pendingPickId.value = id;
      touchStartX.value = t.clientX;
      touchStartY.value = t.clientY;
      touchStartTime.value = Date.now();
      touchMoved.value = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isIPhone.value || pendingPickId.value == null) return;
      const t = e.touches[0];
      const dx = Math.abs(t.clientX - touchStartX.value);
      const dy = Math.abs(t.clientY - touchStartY.value);
      if (dx > TAP_MOVE_THRESHOLD || dy > TAP_MOVE_THRESHOLD) {
        touchMoved.value = true;
      }
    };

    const onTouchEnd = () => {
      if (!isIPhone.value || pendingPickId.value == null) return;
      const elapsed = Date.now() - touchStartTime.value;
      const id = pendingPickId.value;
      pendingPickId.value = null;
      if (!touchMoved.value && elapsed < TAP_TIME_THRESHOLD) {
        onPick(id);
      }
      touchMoved.value = false;
    };

    // pick + safe close
    const onPick = (id: number) => {
      if (closing.value) return;
      imageId.value = id;
      (document.activeElement as HTMLElement | null)?.blur?.();
      closing.value = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(
            () => {
              emit('close');
              closing.value = false;
            },
            isIPhone.value ? 180 : 80
          );
        });
      });
    };

    const onCloseClick = () => {
      if (closing.value) return;
      (document.activeElement as HTMLElement | null)?.blur?.();
      closing.value = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(
            () => {
              emit('close');
              closing.value = false;
            },
            isIPhone.value ? 180 : 80
          );
        });
      });
    };

    const { t } = useLabel();

    return {
      t,
      categoryId,
      searchMode,
      imageId,
      selectedCategoryIds,
      query,
      closing,
      pendingPickId,
      touchStartX,
      touchStartY,
      touchStartTime,
      touchMoved,
      selectedCategories,
      isIPhone,
      imageRefName,
      imagesInCategory,
      onCardClick,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onPick,
      onCloseClick,
    };
  },
});
</script>

<style scoped>
.image-picker-scroll {
  max-height: calc(100vh - 64px);
  min-height: 40vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
  padding-bottom: env(safe-area-inset-bottom);
}
.touch-pan-y {
  touch-action: pan-y;
}

/* Scoped fix for select alignment (no global CSS) */
.category-select {
  max-width: 300px;
}
/* deep selectors for Vuetify internals */
</style>
